import { z } from "zod";
import { slugString } from "./slug.js";

// SessionResult = computed summary of a saved Session, derived from the
// envelope + the LogSet rows attached to it. Stored as its own DDB item
// (pk USER#<sub>, sk SESSION_RESULT#<startedAtMs>#<sessionId>) so the
// post-session widget + share card can fetch one row instead of
// re-aggregating sets on every render.
//
// Recomputed lazily on read: GET /api/session-results/:sessionId checks
// for a cached row first and computes-then-stores on miss. SL-imported
// sessions and pre-feature sessions hit the same path. The bulk recompute
// endpoint exists for the SL import chronological pass so PR + delta
// flags are computed against all prior imported history rather than
// just the most recent few.
//
// PR + delta semantics are STICKY (frozen at compute time): once a
// SessionResult row is written, a later session that exceeds its e1RM
// or volume does NOT retroactively clear the isPrE1rm flag here. The
// historical "this WAS the PR at the moment" record stays accurate.
// Re-running recompute over the chronological history regenerates the
// same flags deterministically because we feed the function only
// strictly-prior sessions.

// Per-exercise breakdown. One entry per distinct exerciseSlug in the
// session. Fields with null tolerate sessions where the exercise has
// no e1RM-eligible sets (cardio-only, fail-only, p1rm-only, etc).
export const ExerciseResult = z.object({
  exerciseSlug: z.string().min(1).max(64),
  // EQ-1-HIST — WHICH physical machine produced this row, when the athlete
  // named one. Absent = the Default instance, which is what every row
  // written before EQ-1-HIST carries and what an untagged entry still
  // carries today: the reader treats "no instance" as the Default track, so
  // legacy rows stay valid WITHOUT a backfill (same contract as topSetRir
  // below, and measured: `equipment-instance` appears 0 times in 37.032
  // production rows as of 2026-08-19, so a backfill would write an empty
  // field on every row).
  //
  // ⭐ Splitting this axis is the point: two machines' kilos are not
  // comparable, so one series per (slug, instance) is the only honest
  // reading — while volume-per-muscle deliberately stays single, because
  // the work on the muscle sums regardless of which machine did it
  // (next/equipment-model.md §8.9).
  equipmentInstance: z.string().min(1).max(80).optional(),
  // Snapshot — server stamped this on legacy rows for back-compat, but
  // clients no longer READ it for display. Display names resolve at
  // render time via the foreign key exerciseSlug against the live
  // Exercise catalog (pickLocalizedText(catalog[slug].name, lang)). Per
  // [[feedback_no_denormalized_display_snapshots]] — renames and
  // catalog translations propagate to every historical surface
  // automatically. New API writes drop the field; legacy rows keep it
  // as the deleted-row fallback only.
  exerciseName: z.string().min(1).max(120).optional(),
  // Top e1RM across all e1RM-eligible sets of this exercise in the
  // session. Null if no eligible set existed.
  topE1rmKg: z.number().min(0).max(5000).nullable(),
  // The set that produced topE1rmKg (or the heaviest weighted set when
  // no e1RM-eligible set existed, or null when this exercise has no
  // weighted sets at all — pure cardio / pure p1rm).
  topSet: z
    .object({
      weightKg: z.number().min(0).max(2000),
      reps: z.number().int().min(0).max(999),
    })
    .nullable(),
  // Representative effort of the set that produced topE1rmKg: its RIR,
  // or null when that set carried no RIR or no e1RM-eligible set existed.
  // Drives effort-aware regression detection
  // (Progress) so a deliberately lighter/high-rep block reads as a plateau,
  // not a decline (Antenore 2026-06-26). `.default(null)` ⇒ legacy SESSION_
  // RESULT rows computed before this field parse as null until backfilled.
  topSetRir: z.number().int().min(0).max(10).nullable().default(null),
  totalVolumeKg: z.number().min(0).max(1_000_000),
  // Isometric/hold metrics (2026-06-09 load model). tutSec = time under
  // tension (Σ duration of this exercise's timed sets); impulseKgS = Σ
  // (effective load × seconds) = the physiologically-correct isometric
  // load (kg·s). 0 for non-timed work. `.default(0)` so legacy SESSION_
  // RESULT rows (computed before this field) still parse.
  tutSec: z.number().min(0).max(1_000_000).default(0),
  impulseKgS: z.number().min(0).max(100_000_000).default(0),
  setCount: z.number().int().min(0).max(200),
  // Effort-filtered subset of setCount: counted sets trained at or below
  // EFFORT_SUBMAXIMAL_RIR (RIR ≤ 2 — the same submaximal threshold the
  // regression detector uses, progressTierOne.ts). A "hard set" is the
  // hypertrophy accumulator unit (AP-2). Sets with no RIR are NOT hard
  // (effort unknown ≠ hard). `.default(0)` ⇒ legacy SESSION_RESULT rows
  // computed before this field parse as 0 until a recompute backfills
  // them; effort data is historically thin so this is forward-correct.
  hardSetCount: z.number().int().min(0).max(200).default(0),
  // Sticky PR flags — frozen at compute time. See module-level comment.
  isPrE1rm: z.boolean(),
  isPrVolume: z.boolean(),
  // Deltas vs the most-recent prior session that included this exercise.
  // Null when there was no prior session (first-ever logging of the
  // exercise) or when the comparison metric was null on either side.
  deltaE1rmKg: z.number().nullable(),
  deltaVolumeKg: z.number().nullable(),
  deltaTopReps: z.number().int().nullable(),
  // Traceability — the sessionId we computed the deltas against. Null
  // when there was no prior session. Lets the widget show "vs your
  // session on 2026-04-12" without re-querying history.
  previousSessionId: z.string().min(1).max(128).nullable(),
});
export type ExerciseResult = z.infer<typeof ExerciseResult>;

// Sparse partial record — only muscle groups that actually accumulated
// volume in the session carry a key. Volume is kg×reps×sides aggregated
// across all weighted sets, distributed to canonical muscle slugs via
// the exercise catalog's `muscleContrib` map (slice #4, 2026-05-28).
// Producers MUST emit catalog vocabulary (CatalogMuscleSlug, 19 slugs);
// the schema is permissive on read so legacy rows written before
// slice #4 (using the ~20-slug bodymap vocabulary) still parse.
// Renderers call `normalizeVolumeByMuscle` from volumeByMuscle.ts
// before iterating, which remaps legacy keys into catalog vocab; slugs
// that have no canonical counterpart (head, neck, tibialis) get
// dropped at the renderer layer.
export const VolumeByMuscle = z.record(
  slugString(64),
  z.number().min(0).max(1_000_000),
);
export type VolumeByMuscle = z.infer<typeof VolumeByMuscle>;

export const SessionResult = z.object({
  sessionId: z.uuid(),
  userSub: z.string().min(1).max(128),
  computedAt: z.iso.datetime(),

  // Aggregate metrics. durationMinutes is null when the source Session
  // has no endedAt (in-progress or legacy pre-1.66.10 backfill miss);
  // densityKgPerMin propagates the null in that case.
  totalVolumeKg: z.number().min(0).max(10_000_000),
  durationMinutes: z.number().min(0).max(1440).nullable(),
  densityKgPerMin: z.number().min(0).max(100_000).nullable(),
  // Session totals for timed/isometric work (2026-06-09 load model).
  // totalTutSec = Σ time under tension; totalImpulseKgS = Σ (load × s).
  // `.default(0)` keeps legacy rows parseable.
  totalTutSec: z.number().min(0).max(10_000_000).default(0),
  totalImpulseKgS: z.number().min(0).max(1_000_000_000).default(0),

  // Derived display label. Top one or two muscle groups by volume in
  // the session, joined with " · " — e.g. "QUAD · POSTERIOR" for a
  // lower-body day. Falls back to "SESSION" when volumeByMuscle is
  // empty (cardio-only / pure isolation day with no catalog hits).
  sessionLabel: z.string().min(1).max(80),

  volumeByMuscle: VolumeByMuscle,
  exerciseResults: z.array(ExerciseResult).max(50),
});
export type SessionResult = z.infer<typeof SessionResult>;
